import { Add, DeleteOutline, EditOutlined, SaveOutlined } from '@mui/icons-material';
import { Box, Button, Card, CardContent, TextField, Typography } from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { selectFolders } from '../../../../redux/reducers/folderReducer';
import {
  deleteProjectConfigQuestion,
  saveProjectConfiguration,
} from '../../../../services/dealGPT';
import { StyledIconButton } from '../../../../theme/CustomComponents';
import { ProjectConfigQuestion } from '../../../../Types/dealGPT';
import { AnalyticsEvent, useAnalytics } from '../../../Providers/AnalyticsProvider';

interface KeyQuestionsCardProps {
  keyQuestions: ProjectConfigQuestion[];
  refetchProjectConfig: () => void;
}

const KeyQuestionsCard: React.FC<KeyQuestionsCardProps> = ({
  keyQuestions,
  refetchProjectConfig,
}) => {
  const [questions, setQuestions] = useState<ProjectConfigQuestion[]>(keyQuestions);
  const [isEditing, setIsEditing] = useState(false);
  const lastQuestionRef = useRef<HTMLInputElement | null>(null);
  const analytics = useAnalytics();
  const { currentFolder } = useSelector(selectFolders);

  const onAddQuestion = useCallback(() => {
    analytics.event(
      AnalyticsEvent.WM_IA_PROJECT_OVERVIEW_KEY_ADD,
      {
        id: currentFolder?.projectId,
      },
      {
        projectId: currentFolder?.projectId,
      }
    );
    const initialQuestion: ProjectConfigQuestion = {
      id: undefined,
      question: '',
      projectId: currentFolder?.projectId ?? '',
      deleted: false,
    };

    setQuestions((prev) => {
      const updatedQuestions = [...prev, { ...initialQuestion, isNew: true }];
      return updatedQuestions;
    });
  }, [currentFolder?.projectId, analytics]);

  const onChangeQuestion = useCallback((question: ProjectConfigQuestion, newQuestion: string) => {
    setQuestions((prev) =>
      prev.map((q) => (q === question ? { ...q, question: newQuestion, isModified: true } : q))
    );
  }, []);

  const deleteKeyQuestion = useCallback(
    async (question: ProjectConfigQuestion) => {
      analytics.event(
        AnalyticsEvent.WM_IA_PROJECT_OVERVIEW_KEY_DELETE,
        {
          id: currentFolder?.projectId,
        },
        {
          projectId: currentFolder?.projectId,
        }
      );
      if (question.id && currentFolder?.projectId) {
        await deleteProjectConfigQuestion(currentFolder?.projectId, question.id);
      }

      setQuestions((prev) => prev.filter((q) => q !== question));
    },
    [currentFolder?.projectId, analytics]
  );

  const saveKeyQuestion = useCallback(
    async (question: ProjectConfigQuestion) => {
      analytics.event(
        AnalyticsEvent.WM_IA_PROJECT_OVERVIEW_KEY_SAVE,
        {
          id: currentFolder?.projectId,
        },
        {
          projectId: currentFolder?.projectId,
        }
      );
      if (!question.isNew && !question.isModified) return;

      if (!question.question.trim()) return;

      if (!currentFolder?.projectId) return;

      // Save the updated question
      try {
        const result = await saveProjectConfiguration(currentFolder.projectId, {
          keyQuestions: [question],
          projectId: currentFolder.projectId,
        });

        // Update the questions array with the saved question
        if (result.length > 0) {
          setQuestions(result);
        } else {
          enqueueSnackbar('Did not save key questions. Please try again.', {
            variant: 'info',
            autoHideDuration: 3000,
          });
        }
      } catch (error) {
        enqueueSnackbar('Failed to save key question. Please try again.', {
          variant: 'error',
          autoHideDuration: 3000,
        });
      }
    },
    [currentFolder?.projectId, analytics]
  );

  useEffect(() => {
    setQuestions(keyQuestions);
  }, [keyQuestions]);

  // Auto-scroll to the last question whenever the `questions` array is updated
  useEffect(() => lastQuestionRef.current?.scrollIntoView({ behavior: 'smooth' }));

  return (
    <Card
      sx={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <CardContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h5" sx={{ color: '#f900d3' }}>
              Key Questions
            </Typography>
            <StyledIconButton
              onClick={() => {
                if (isEditing) refetchProjectConfig();
                setIsEditing(!isEditing);
              }}
              type="button"
            >
              {isEditing ? <SaveOutlined /> : <EditOutlined />}
            </StyledIconButton>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, padding: 1 }}>
            {isEditing ? (
              <>
                {questions.map((question, index) => (
                  <Box key={question.id} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TextField
                      label={`Enter Key Question ${index + 1}`}
                      onChange={(e) => onChangeQuestion(question, e.target.value)}
                      required
                      fullWidth
                      margin="normal"
                      variant="outlined"
                      multiline
                      value={question.question}
                      onBlur={() => saveKeyQuestion(question)}
                      inputRef={
                        index === questions.length - 1 && !question.id ? lastQuestionRef : null
                      }
                    />
                    <StyledIconButton onClick={() => deleteKeyQuestion(question)}>
                      <DeleteOutline />
                    </StyledIconButton>
                  </Box>
                ))}
                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                  <Button onClick={onAddQuestion} startIcon={<Add />}>
                    Add Key Question
                  </Button>
                </Box>
              </>
            ) : (
              questions.map((question) => (
                <Box key={question.id} sx={{ borderBottom: '1px solid #ccc', paddingBottom: 1 }}>
                  <Typography key={question.id} variant="body2">
                    {question.question}
                  </Typography>
                </Box>
              ))
            )}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default KeyQuestionsCard;
